import React from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { remittanceActions } from "../../state/remittanceSlice";
import "react-loading-skeleton/dist/skeleton.css";
import Button from "../../../../components/ui/Button";
import { styles } from "./styles";
import SelectDropdown from "../../components/ui/SelectDropdown";
import { Download, Eye, Signature } from "lucide-react";
import { toast } from "react-toastify";
import MixpanelConfig from "../../../../constants/MixpanelConfig";
import {
  isDigioSignedLater,
  isDigioSigningComplete,
  isDigioSignLinkSent,
} from "../../utils/digioStatus";
import { AppStrings, isAdIIPurpose } from "../../utils";

interface ReviewOverviewProps {
  viewORDownloadA2Form: (type: string) => void;
  initializeTransaction: () => void;
  isDigioEsignEnabled?: boolean;
}

const ReviewOverview: React.FC<ReviewOverviewProps> = ({
  viewORDownloadA2Form,
  initializeTransaction,
  isDigioEsignEnabled = false,
}) => {
  const dispatch = useAppDispatch();

  const authState = useAppSelector((state) => state.auth);
  const remitterState = useAppSelector((state) => state.remittance);
  const { calculator, remitter, review, transactionDetail } = useAppSelector(
    (state) => state.remittance
  );
  const { remitterSignedStatus } = review;
  const digioRecord = review?.digioEsign?.data;
  const digioStatus = digioRecord?.status;

  const isAdIINewKyc =
    isAdIIPurpose(calculator?.amountCalculation?.purposeDropdown?.adCategory) &&
    Boolean(
      remitter?.getTransLoggedInVendorConfig?.data?.transactionVendor?.adIINewKycRequired
    );

  const isSigned = isDigioEsignEnabled
    ? isDigioSigningComplete(digioStatus)
    : Boolean(remitterSignedStatus.data?.signed);
  const isSignLinkSent = isDigioEsignEnabled
    ? isDigioSignLinkSent(digioStatus)
    : Boolean(remitterSignedStatus.data?.signLinkSent);
  const isSignedLater = isDigioEsignEnabled
    ? isDigioSignedLater(digioRecord)
    : Boolean(remitterSignedStatus.data?.signedLatter);

  const lanNumberLength = review?.reviewDetails?.lanNumber?.trim()?.length ?? 0;

  const expectedLanLength = Number(authState?.lanLength || 12);

  const payButtonDisable =
    review?.remitterSignedStatus?.loading ||
    review?.consentData?.loading ||
    review?.remitterSignedStatus?.error ||
    review?.consentData?.error ||
    !review?.reviewDetails?.consentForm ||
    !review?.reviewDetails?.termsAndConditions ||
    review?.reviewDetails?.sourceOfFundsDropdown == null ||
    (authState?.lanMandatory && lanNumberLength !== expectedLanLength);

  const payEsignedDisable = !(isSigned || isSignLinkSent || isSignedLater);
  const isSourceOfFundsLocked =
    Boolean(isDigioEsignEnabled) &&
    (isSignLinkSent ||
      isSigned ||
      digioStatus === "TOKEN_EXPIRED" ||
      digioStatus === "LINK_EXPIRED");

  const openDigioEsignModal = () => {
    dispatch(remittanceActions.setModalType("DIGIO_ESIGN_MODAL"));
    if (review?.digioEsign?.data?.uniqueKey) {
      dispatch(remittanceActions.fetchDigioStatusRequested());
    }
  };

  const handleDownloadA2Form = () => {
    if (isSigned) {
      viewORDownloadA2Form("download");
      return;
    }

    if (isDigioEsignEnabled && review?.reviewDetails?.sourceOfFundsDropdown == null) {
      toast.error("Please select source of funds", { position: "top-center" });
      return;
    }

    if (isDigioEsignEnabled && isSignLinkSent) {
      openDigioEsignModal();
      return;
    }

    if (!isDigioEsignEnabled && isSignLinkSent) {
      viewORDownloadA2Form("download");
      return;
    }

    const userKey = remitter?.selectedRemitter?.userKey;
    if (!userKey) {
      return;
    }

    dispatch(
      remittanceActions.setModalType(
        isDigioEsignEnabled ? "DIGIO_ESIGN_MODAL" : "REMITTER_ESIGN_MODAL"
      )
    );

    if (isDigioEsignEnabled) {
      if (review?.digioEsign?.data?.uniqueKey) {
        dispatch(remittanceActions.fetchDigioStatusRequested());
      }
    } else {
      dispatch(remittanceActions.fetchRemitterSignedStatusRequested(userKey));
    }
  };

  const loading =
    review?.remitterSignedStatus?.loading ||
    review?.consentData?.loading ||
    (isDigioEsignEnabled && review?.digioEsign?.loading) ||
    calculator?.getTransactionSummary?.loading;

  return (
    <div className={styles.reviewOverview.wrapper}>
      <div className={styles.reviewOverview.container}>
        <div className="mt-2 py-4 space-y-5">
          <div className="flex items-center gap-3">
            <div
              id="purpose"
              className="h-12 w-[460px] border border-gray-300 flex items-center gap-2 rounded-md p-4 bg-gray-100 text-[14px] cursor-not-allowed"
            >
              <span className="float-left font-semibold">Purpose of Payment:</span>
              {calculator?.amountCalculation?.transaction_purpose}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div
              id="vendorCode"
              className="h-12 w-[460px] border border-gray-300 flex items-center gap-2 rounded-md p-4 bg-gray-100 text-[14px] cursor-not-allowed"
            >
              <span className="float-left font-semibold">Vendor Code:</span>
              {calculator?.amountCalculation?.vendorDropdown?.vendorCode}
            </div>
          </div>

          {/* LAN/UTR fields are visible for all vendors for now. */}
          <>
            <div className="flex items-center gap-3">
              <div
                id="lanNO"
                className="h-12 w-[460px] border border-gray-300 flex items-center gap-2 rounded-md p-4 bg-gray-100 text-[14px]"
              >
                <span className="font-semibold whitespace-nowrap">
                  LAN No:
                  <span className="text-red-100 text-lg pl-1">
                    {authState?.lanMandatory ? "*" : ""}
                  </span>
                </span>
                <input
                  type="text"
                  value={review?.reviewDetails?.lanNumber}
                  className="w-full bg-gray-100 text-[14px] text-black outline-none flex-1 mt-1"
                  onChange={(e) => {
                    const input = e.target.value;
                    const alphanumericOnly = input.replace(/[^a-zA-Z0-9]/g, "");
                    if (alphanumericOnly.length <= expectedLanLength) {
                      dispatch(remittanceActions.setReviewDetailsInput({ lanNumber: input }));
                    }
                    MixpanelConfig.trackEvent(MixpanelConfig.eventNames.New_Review_LAN_Enter, {
                      authState: authState,
                      remitterState: remitterState,
                      lanValue: alphanumericOnly,
                    });
                  }}
                  placeholder={`Enter ${expectedLanLength} digit LAN Number`}
                  required={authState?.lanMandatory}
                  minLength={expectedLanLength}
                  maxLength={expectedLanLength}
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div
                id="utrNO"
                className="h-12 w-[460px] border border-gray-300 flex items-center gap-2 rounded-md p-4 bg-gray-100 text-[14px]"
              >
                <span className="font-semibold whitespace-nowrap">UTR No: </span>
                <input
                  type="text"
                  value={review?.reviewDetails?.utrNumber}
                  className="flex-1 w-full bg-gray-100 text-[14px] text-black outline-none"
                  onChange={(e) => {
                    const input = e.target.value;
                    dispatch(remittanceActions.setReviewDetailsInput({ utrNumber: input }));
                  }}
                  placeholder="Enter UTR Number"
                />
              </div>
            </div>
          </>
          {/* )} */}

          <span className="float-left font-semibold">
            Source of Funds
            <span className="text-red-100 text-sm pl-1">{"*"}</span>
          </span>
          <SelectDropdown
            data={review?.sourceOfFundsData ?? []}
            value={review?.reviewDetails?.sourceOfFundsDropdown ?? null}
            onChange={(response) => {
              dispatch(
                remittanceActions.setReviewDetailsInput({ sourceOfFundsDropdown: response })
              );
              if (
                isDigioEsignEnabled &&
                response &&
                !isSourceOfFundsLocked &&
                !isSignedLater
              ) {
                openDigioEsignModal();
              }
            }}
            getLabel={(item) => item.label}
            getValue={(item) => item.value}
            width={"460px"}
            placeHolderFontSize={"16px"}
            textSize={"16px"}
            fontWeight={400}
            backgroundColor="#f3f4f6"
            placeholder={"Select Source of Funds"}
            isSearchable={true}
            isLoading={calculator?.purpose?.loading}
            isDisabled={isSourceOfFundsLocked}
          />
          {/* {isSourceOfFundsLocked ? ( */}
          {isAdIINewKyc  ? (
            <p style={{ marginTop: "2px" }} className="w-[460px] text-sm text-gray-500">
              {AppStrings.REVIEW_SCREEN.SOURCE_OF_FUNDS_LOCKED}
            </p>
          ) : null}
          {/* ) : null} */}

          <div className="flex gap-2">
            <Button
              bgColor={"bg-[#006EFF]"}
              fontStyle={{
                fontFamily: "font-Inter",
                fontSize: "text-sm",
                fontWeight: "font-bold",
                fontColor: "text-white",
              }}
              borderStyle={{ borderRadius: "rounded-lg" }}
              className={"p-3 hover:bg-blue-600 min-w-[10rem]"}
              onClickHandler={() => viewORDownloadA2Form("view")}
              disabled={review?.viewA2Form?.loading}
              isLoading={review?.viewA2Form?.loading}
            >
              <div className="flex items-center gap-1">
                <Eye width={15} height={15} />
                {AppStrings.REVIEW_SCREEN.VIEW_A2_FORM}
              </div>
            </Button>
            <Button
              bgColor={"bg-[#006EFF]"}
              fontStyle={{
                fontFamily: "font-Inter",
                fontSize: "text-sm",
                fontWeight: "font-bold",
                fontColor: "text-white",
              }}
              borderStyle={{ borderRadius: "rounded-lg" }}
              className={"p-3 hover:bg-blue-600 min-w-[10rem]"}
              onClickHandler={handleDownloadA2Form}
              disabled={review?.downloadA2Form?.loading}
              isLoading={review?.downloadA2Form?.loading}
            >
              <div className="flex items-center gap-1">
                {isDigioEsignEnabled || (!isSigned && !isSignLinkSent) ? (
                  <Signature width={15} height={15} />
                ) : (
                  <Download width={15} height={15} />
                )}
                {isDigioEsignEnabled
                  ? AppStrings.REMITTER_ESIGN_MODAL.SIGN_A2_FORM
                  : isSigned || isSignLinkSent
                    ? AppStrings.REVIEW_SCREEN.DOWNLOAD_A2_FORM
                    : AppStrings.REMITTER_ESIGN_MODAL.SIGN_A2_FORM}

                {payEsignedDisable && <span className="text-red-100 text-sm">{"*"}</span>}
              </div>
            </Button>
            {loading && (
              <div className="flex justify-center items-center">
                <div className="animate-spin h-6 w-6 border-2 border-t-blue-500 rounded-full" />
              </div>
            )}
          </div>

          <div className="flex flex-col space-y-2">
            <div className="flex">
              <input
                type="checkbox"
                checked={review?.reviewDetails?.termsAndConditions}
                onChange={() => {
                  dispatch(
                    remittanceActions.setReviewDetailsInput({
                      termsAndConditions: !review?.reviewDetails?.termsAndConditions,
                    })
                  );
                }}
                required
                className="review-checkbox w-11 h-11"
                style={{ cursor: "pointer" }}
              />

              <label className="ml-2 font-medium font-sans text-[16px] flex items-center">
                <p>
                  I confirm that the information captured in the A2 Form is accurate
                  <span> and agree to the</span>
                  <span
                    className=" text-blue-500 cursor-pointer hover:underline"
                    onClick={() => {
                      dispatch(remittanceActions.setModalType("TERMS_AND_CONDITIONS"));
                    }}
                  >
                    {" Terms & Conditions. "}
                  </span>
                </p>
              </label>
            </div>
            <div className="flex">
              <input
                type="checkbox"
                required
                checked={review?.reviewDetails?.consentForm}
                className="review-checkbox w-[70px] h-8"
                onClick={() => {
                  dispatch(
                    remittanceActions.setReviewDetailsInput({
                      consentForm: !review?.reviewDetails?.consentForm,
                    })
                  );
                }}
                style={{ cursor: "pointer" }}
              />
              <label className="ml-2 font-medium font-sans text-[16px] flex items-center">
                <p>
                  As the authorized representative of the Remitter, I hereby acknowledge and accept
                  the defined
                  <span
                    className="text-blue-600 cursor-pointer hover:underline"
                    onClick={() => {
                      dispatch(remittanceActions.setModalType("CONSENT_FORM"));
                    }}
                  >
                    {" "}
                    roles, responsibilities, and timelines{" "}
                  </span>
                  associated with the transaction.{" "}
                </p>
              </label>
            </div>
          </div>

          <Button
            bgColor={
              payButtonDisable || payEsignedDisable || transactionDetail?.loading
                ? "bg-primary-200 opacity-50"
                : "bg-primary-200"
            }
            fontStyle={{
              fontFamily: "font-Inter",
              fontSize: "text-sm",
              fontWeight: "font-bold",
              fontColor: "text-white",
            }}
            borderStyle={{ borderRadius: "rounded-lg" }}
            className={`p-4 ${
              payButtonDisable || payEsignedDisable || transactionDetail?.loading
                ? "hover:bg-primary-200 opacity-50"
                : "hover:bg-red-200"
            } min-w-[21rem] w-[450px] items-center rounded-3xl`}
            onClickHandler={initializeTransaction}
            disabled={payButtonDisable || payEsignedDisable || transactionDetail?.loading}
            isLoading={transactionDetail?.loading}
          >
            <div className="flex items-center font-bold">
              <p className="font-bold text-[20px]">{"PAY"}</p>
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewOverview;
